import type { MoneyHalalas } from "@sirdab/returns-shared";
import { Alert, AlertDescription, AlertTitle } from "@sirdab/ui";

import { useMessages } from "../i18n";
import { Money } from "./Money";

export interface FeeDisclosureProps {
  /** Total across the requested lines, integer halalas. */
  totalHalalas: MoneyHalalas;
  /**
   * Marks this as the second of the two disclosures (policy check 9). Both render the
   * same figure; only the test IDs differ, so a test can prove both points exist.
   */
  point: "resolution" | "review";
}

/**
 * Fee disclosure. Policy check 9 requires the fee before submit AND again at review — a
 * shopper must never meet a deduction for the first time after the fact.
 *
 * Zero is a real disclosure, not an absence: "no return fee" is stated rather than left
 * to inference. No timeline is ever promised here; we do not move money (ADR-003).
 */
export function FeeDisclosure({
  totalHalalas,
  point,
}: FeeDisclosureProps): React.JSX.Element {
  const { t } = useMessages();
  const free = totalHalalas === 0;

  return (
    <Alert variant={free ? "default" : "warning"}>
      <AlertTitle>{t("fee.disclosure_title")}</AlertTitle>
      <AlertDescription data-fee-disclosure={point}>
        {free ? (
          <p>{t("fee.none")}</p>
        ) : (
          <>
            <p className="flex items-baseline justify-between gap-3">
              <span>{t("fee.total_label")}</span>
              <Money halalas={totalHalalas} className="font-semibold" />
            </p>
            <p>{t("fee.deducted_note")}</p>
          </>
        )}
        <p className="text-caption-quiet text-muted-foreground">
          {t("ui.server_confirms")}
        </p>
      </AlertDescription>
    </Alert>
  );
}
